import type { DoseStatus, Lang } from '../types';

const DAY_MS = 86_400_000;

export function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

export function toIsoDate(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function parseDate(s: string): Date {
  return s.length === 10 ? new Date(`${s}T00:00:00`) : new Date(s);
}

export function dayDiff(from: Date, to: Date): number {
  const a = new Date(from);
  a.setHours(0, 0, 0, 0);
  const b = new Date(to);
  b.setHours(0, 0, 0, 0);
  return Math.round((b.getTime() - a.getTime()) / DAY_MS);
}

export function daysAgoAt(daysAgo: number, h: number, m = 0): string {
  const d = addDays(startOfToday(), -daysAgo);
  d.setHours(h, m, 0, 0);
  return d.toISOString();
}

const locale = (lang: Lang) => (lang === 'hi' ? 'hi-IN' : 'en-IN');

export function formatDate(s: string, lang: Lang): string {
  return parseDate(s).toLocaleDateString(locale(lang), { day: 'numeric', month: 'short' });
}

export function formatLongDate(d: Date, lang: Lang): string {
  return d.toLocaleDateString(locale(lang), { weekday: 'long', day: 'numeric', month: 'long' });
}

export function formatWeekday(s: string, lang: Lang): string {
  return parseDate(s).toLocaleDateString(locale(lang), { weekday: 'short' });
}

export function formatTime(s: string, lang: Lang): string {
  return parseDate(s).toLocaleTimeString(locale(lang), { hour: 'numeric', minute: '2-digit' });
}

// Default status for a scheduled visit `diff` days from today (negative = past).
export function statusByDiff(diff: number): DoseStatus {
  return diff < -3 ? 'attended' : diff <= 0 ? 'due' : 'upcoming';
}

export function minutesAgo(n: number): string {
  return new Date(Date.now() - n * 60_000).toISOString();
}

/** The time now in India as 'HH:MM' (clinic diaries are in India time). */
export function istHHMM(d = new Date()): string {
  const p = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', hour12: false }).formatToParts(d);
  const v = (t: string) => p.find((x) => x.type === t)?.value ?? '00';
  return `${v('hour') === '24' ? '00' : v('hour')}:${v('minute')}`;
}

export function istHour(): number {
  return Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Asia/Kolkata' }).format(new Date()));
}

export function isToday(s: string): boolean {
  return dayDiff(new Date(), parseDate(s)) === 0;
}
